import { z } from 'zod'
import { fromLocalInput, toLocalInput } from './meta'
import {
  ANNOUNCEMENT_AUDIENCES,
  ANNOUNCEMENT_CATEGORIES,
  ANNOUNCEMENT_PRIORITIES,
  type Announcement,
  type AnnouncementPayload,
} from './types'

export type PublishMode = 'draft' | 'now' | 'schedule'

/** Editor form. Dates are datetime-local strings; `publishMode` decides how publishAt is sent. */
export const announcementSchema = z
  .object({
    title: z.string().trim().min(1, 'Give the announcement a title.').max(200, 'Use at most 200 characters.'),
    body: z
      .string()
      .trim()
      .min(1, 'Write the announcement text.')
      .max(10_000, 'Use at most 10,000 characters.'),
    category: z.enum(ANNOUNCEMENT_CATEGORIES, { error: 'Choose a category.' }),
    audience: z.enum(ANNOUNCEMENT_AUDIENCES, { error: 'Choose who should receive it.' }),
    priority: z.enum(ANNOUNCEMENT_PRIORITIES),
    publishMode: z.enum(['draft', 'now', 'schedule']),
    publishAt: z.string(),
    expireAt: z.string(),
    isPinned: z.boolean(),
    isPublic: z.boolean(),
    classIds: z.array(z.number().int().positive()),
    attachmentFileId: z.number().int().positive().nullable(),
  })
  .superRefine((v, ctx) => {
    if (v.publishMode === 'schedule' && !v.publishAt)
      ctx.addIssue({ code: 'custom', path: ['publishAt'], message: 'Pick when to publish.' })
    if (v.audience === 'class' && v.classIds.length === 0)
      ctx.addIssue({ code: 'custom', path: ['classIds'], message: 'Choose at least one class.' })
    // Past-expiry checks for "publish now" need the server clock and the stored value, so the API owns them.
    if (
      v.expireAt &&
      v.publishMode === 'schedule' &&
      v.publishAt &&
      new Date(v.expireAt).getTime() <= new Date(v.publishAt).getTime()
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['expireAt'],
        message: 'The expiry must be after the publish date.',
      })
    }
  })

export type AnnouncementFormValues = z.infer<typeof announcementSchema>

export function toFormValues(a: Announcement | null): AnnouncementFormValues {
  const mode: PublishMode =
    !a || a.status === 'draft' ? (a ? 'draft' : 'now') : a.status === 'scheduled' ? 'schedule' : 'now'
  return {
    title: a?.title ?? '',
    body: a?.body ?? '',
    category: a?.category ?? 'general',
    audience: a?.audience ?? 'all',
    priority: a?.priority ?? 'normal',
    publishMode: mode,
    publishAt: toLocalInput(a?.publishAt ?? null),
    expireAt: toLocalInput(a?.expireAt ?? null),
    isPinned: a?.isPinned ?? false,
    isPublic: a?.isPublic ?? false,
    classIds: a?.classes.map((c) => c.id) ?? [],
    attachmentFileId: a?.attachment?.id ?? null,
  }
}

/**
 * Form → API payload. "Publish now" keeps an existing past publish date (so editing a live notice
 * does not bump it to the top) and otherwise uses the current time.
 */
export function toPayload(v: AnnouncementFormValues, existing: Announcement | null): AnnouncementPayload {
  let publishAt: string | null = null
  if (v.publishMode === 'schedule') publishAt = fromLocalInput(v.publishAt)
  if (v.publishMode === 'now') {
    const keep = existing?.publishAt && new Date(existing.publishAt).getTime() <= Date.now()
    publishAt = keep ? existing.publishAt : new Date().toISOString()
  }
  return {
    title: v.title,
    body: v.body,
    category: v.category,
    audience: v.audience,
    priority: v.priority,
    publishAt,
    expireAt: fromLocalInput(v.expireAt),
    isPinned: v.isPinned,
    isPublic: v.audience === 'all' && v.isPublic,
    classIds: v.audience === 'class' ? v.classIds : [],
    attachmentFileId: v.attachmentFileId,
  }
}

export const ANNOUNCEMENT_FIELDS = [
  'title',
  'body',
  'category',
  'audience',
  'priority',
  'publishAt',
  'expireAt',
  'isPinned',
  'isPublic',
  'classIds',
  'attachmentFileId',
] as const
